'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import Cookies from 'js-cookie';
import { useCurrentLocale } from '@locales/client';

import { useAuthModal } from '@/shared/hooks/useAuthModal';
import {
  useUserHasPaymentInfo,
  useUserAuthStatus,
  useIsAuth,
} from '@/shared/providers/user-provider/use-user';
import { BannerPrompts } from '@/features/data-management/ui/banner-prompts/banner-prompts';
import { useBanners } from './model/hooks';
import { Wrapper } from './styles';
import { BonusBanner } from './ui/bonus-banner';
import { BannerSkeleton } from './ui/banner-skeleton';
import { SectionErrorBoundary } from '@/shared/ui/section-error-boundary';

const DynamicMotivationalBlock = dynamic(
  async () =>
    import('./ui/motivational-block').then(
      (module) => module.MotivationalBlock
    ),
  {
    ssr: false,
    loading: () => <BannerSkeleton />,
  }
);

const loadNewsSwiper = () =>
  import('./ui/news-swiper').then((module) => module.NewsSwiper);

const DynamicNewsSwiper = dynamic(loadNewsSwiper, {
  ssr: false,
  loading: () => <BannerSkeleton />,
});

const News = () => {
  const locale = useCurrentLocale();
  const isAuth = useIsAuth();
  const hasPaymentInfo = useUserHasPaymentInfo();
  const authStatus = useUserAuthStatus();
  const { handleOpenAuth } = useAuthModal();

  const isActiveMotivational = isAuth && !hasPaymentInfo && locale === 'ro';
  const shouldPreloadAuthBanners =
    isAuth || (authStatus === 'checking' && Boolean(Cookies.get('sessionId')));

  useBanners({ enabled: shouldPreloadAuthBanners });

  useEffect(() => {
    if (shouldPreloadAuthBanners) {
      void loadNewsSwiper();
    }
  }, [shouldPreloadAuthBanners]);

  const renderContent = () => {
    const isChecking = authStatus === 'checking';

    if (isChecking || !isAuth) {
      return (
        <>
          <div data-guest-only={isChecking || undefined}>
            <BonusBanner />
          </div>
          {isChecking && (
            <div data-session-only>
              <BannerSkeleton />
            </div>
          )}
        </>
      );
    }

    if (isActiveMotivational) {
      return <DynamicMotivationalBlock />;
    }

    return <DynamicNewsSwiper />;
  };

  const showBannerPrompts = isAuth && !isActiveMotivational;

  return (
    <SectionErrorBoundary
      name={'BannerPrompts_NewsSwiper'}
      fallback={<div>Banners error</div>}
      resetKeys={[isAuth, locale]}
    >
      {showBannerPrompts && <BannerPrompts />}
      <Wrapper
        onClick={authStatus === 'anonymous' ? handleOpenAuth : undefined}
      >
        {renderContent()}
      </Wrapper>
    </SectionErrorBoundary>
  );
};

export { News };
